import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';

import { appMessagesFor } from '@amumin/i18n';
import { cx, Icon } from '@amumin/ui-core';

import styles from './DateTimeField.module.css';
import field from './TextField.module.css';
import {
  addDuration,
  DURATIONS,
  durationBetween,
  formatDuration,
  inRange,
  minutesOfTime,
  parseTime,
} from './time.js';
import { TimePicker, type TimeFieldLabels } from './TimePicker.js';

// Tijdveld met eigen kiezer (verzoek oprichter 2026-09-24: "tijd instellen loopt niet lekker,
// maak dit beter, gebruikersvriendelijker"). Vervangt het native <input type="time"> met zijn
// minuut-voor-minuut-lijst. Je kunt typen ("1530", "15.30", "930", "9u") of op de klok tikken:
// eerst het uur, dan de minuten. Zelfde API als voorheen (value "HH:MM", onInput, label, error,
// hint, min/max/step), zodat elk bestaand gebruik blijft werken.

export interface TimeFieldProps {
  id: string;
  label: string;
  /** "HH:MM" of leeg. */
  value: string;
  /**
   * Nieuwe waarde ("HH:MM"), zodra die vaststaat: bij een keuze in de kiezer, bij een volledig
   * getypte tijd ("15:30", "1530"), en bij verlaten/Enter (genormaliseerd). Een tijd die we niet
   * begrijpen geeft "" (het getypte blijft zichtbaar, met een rustige hint).
   */
  onInput: (value: string) => void;
  error?: string | undefined;
  hint?: string | undefined;
  required?: boolean;
  disabled?: boolean;
  /** Vroegste/laatste tijd ("HH:MM"); daarbuiten zijn knoppen uit en is typen ongeldig. */
  min?: string;
  max?: string;
  name?: string;
  /** Label alleen voor schermlezers (bv. in een lijstrij met de naam ernaast). */
  hideLabel?: boolean;
  /** Zonder ruimte eronder (in een rij of lijst). */
  compact?: boolean;
  /** Bestaat nog voor bestaand gebruik; de waarde komt altijd pas bij een vaste tijd. */
  commitOnChange?: boolean;
  /** Stap in seconden voor de minutenknoppen (900 = alleen kwartieren). Standaard 5 minuten. */
  step?: number;
  /**
   * Begintijd ("HH:MM") voor een eindtijd: de kiezer toont dan duurknoppen ("1 uur · 16:30") en
   * het veld de duur naast het label.
   */
  durationFrom?: string | undefined;
  /** Teksten (i18n `a.timePicker`); zonder: Nederlands of Engels volgens <html lang>. */
  labels?: TimeFieldLabels | undefined;
}

/** Standaardteksten als de pagina ze niet meegeeft (bestaand gebruik zonder `labels`). */
export function defaultTimeLabels(): TimeFieldLabels {
  const lang = typeof document === 'undefined' ? 'nl' : document.documentElement.lang;
  // Golf 3: elke taal van het register (normalizeLocale), met terugval op nl.
  return appMessagesFor(lang).timePicker;
}

/** Volledig getypt en niet meer dubbelzinnig: "15:30", "15.30", "1530", "09:05". */
const COMPLETE = /^\s*(\d{1,2}\s*[:.,uh]\s*\d{2}|\d{4})\s*$/i;

/** Tijdveld: typen of kiezen (TimePicker). Breed genoeg voor "23:59" plus de klokknop. */
export function TimeField({
  id,
  label,
  value,
  onInput,
  error,
  hint,
  required,
  disabled,
  min,
  max,
  name,
  hideLabel,
  compact,
  step,
  durationFrom,
  labels,
}: TimeFieldProps) {
  const text = labels ?? defaultTimeLabels();
  const [draft, setDraft] = useState(value);
  const [invalid, setInvalid] = useState(false);
  const [open, setOpen] = useState(false);
  const lastEmitted = useRef(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  // Na sluiten van de kiezer de focus terug naar de klokknop (niet na "Zelf typen").
  const returnFocus = useRef(false);
  // Op touch opent een tik op het veld de kiezer; na "Zelf typen" krijg je het toetsenbord.
  const typing = useRef(false);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  // Klik met de muis in het veld: de hele tijd selecteren, zodat typen "1530" de oude tijd
  // vervangt in plaats van erachter te plakken (zoals het native tijdveld; review c-ux r1).
  const pointerFocus = useRef(false);
  // Geopend met een tik (touch): na sluiten krijgt de klokknop de focus zonder dikke focusring,
  // anders lijkt er nog iets geselecteerd. Met toetsenbord of muis blijft de ring (review c-ux r1).
  const openedByTouch = useRef(false);
  // Geopend met het toetsenbord (Enter/spatie op de klokknop, Alt+↓): alleen dan meteen een
  // focusring in de kiezer (review c-ux r3).
  const openedByKeyboard = useRef(false);
  const lastPointer = useRef('');

  // Waarde van buiten (bv. Tot schuift mee met Van): het veld volgt, tenzij het de eigen echo is.
  useLayoutEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setDraft(value);
      setInvalid(false);
    }
  }, [value]);

  useEffect(() => {
    if (!open && returnFocus.current) {
      returnFocus.current = false;
      const button = buttonRef.current;
      if (!button) return;
      if (openedByTouch.current) {
        button.setAttribute('data-quiet-focus', '');
        const loud = () => {
          button.removeAttribute('data-quiet-focus');
          button.removeEventListener('blur', loud);
          button.removeEventListener('keydown', loud);
        };
        button.addEventListener('blur', loud);
        button.addEventListener('keydown', loud);
      }
      button.focus({ preventScroll: true });
    }
  }, [open]);

  const emit = (next: string) => {
    lastEmitted.current = next;
    onInput(next);
  };

  /** Getypte tekst vastleggen (verlaten, Enter): normaliseren of een rustige hint tonen. */
  const commit = (raw: string): boolean => {
    const parsed = parseTime(raw);
    if (!parsed.ok || (parsed.value !== '' && !inRange(parsed.value, min, max))) {
      setInvalid(true);
      if (lastEmitted.current !== '') emit('');
      return false;
    }
    setInvalid(false);
    setDraft(parsed.value);
    if (parsed.value !== lastEmitted.current) emit(parsed.value);
    return true;
  };

  const openPicker = (byTouch = false, byKeyboard = false) => {
    if (disabled) return;
    openedByTouch.current = byTouch;
    openedByKeyboard.current = byKeyboard;
    returnFocus.current = true;
    setOpen(true);
  };

  const pick = (next: string) => {
    setDraft(next);
    setInvalid(false);
    if (next !== lastEmitted.current) emit(next);
    setOpen(false);
  };

  const startTyping = () => {
    // Synchroon binnen de tik: dan opent iOS het toetsenbord. Het dialoog is al dicht (TimePicker).
    returnFocus.current = false;
    typing.current = true;
    setOpen(false);
    const input = inputRef.current;
    if (input) {
      input.focus();
      input.select();
    }
  };

  const duration = durationFrom !== undefined ? durationBetween(durationFrom, value) : null;
  const durationText = duration !== null && duration > 0 ? formatDuration(duration, text) : null;
  const beforeStart = duration !== null && duration <= 0 && durationFrom !== undefined;

  let message: { kind: 'error' | 'hint'; text: string } | null = null;
  if (error) message = { kind: 'error', text: error };
  else if (invalid) message = { kind: 'error', text: text.invalid };
  else if (beforeStart)
    message = { kind: 'hint', text: text.beforeStart.replace('{start}', durationFrom ?? '') };
  else if (hint) message = { kind: 'hint', text: hint };
  const messageId = message ? `${id}-${message.kind}` : undefined;
  const durationId = durationText ? `${id}-duration` : undefined;
  const describedBy = [messageId, durationId].filter(Boolean).join(' ') || undefined;
  const pickerId = `${id}-picker`;

  return (
    <div
      class={cx(field.field, styles.time)}
      style={compact ? 'margin-bottom: 0' : undefined}
      data-name={`${id}-field`}
    >
      <div class={cx(styles.labelRow, hideLabel && styles.labelRowHidden)}>
        <label class={hideLabel ? 'fb-sr-only' : cx(field.label, styles.label)} for={id}>
          {label}
        </label>
        {durationText ? (
          <span id={durationId} class={styles.durationBadge} data-name={`${id}-duration`}>
            {durationText}
          </span>
        ) : null}
      </div>
      <div class={styles.control} ref={boxRef}>
        <input
          ref={inputRef}
          id={id}
          name={name ?? id}
          class={cx(field.input, styles.input, styles.timeInput)}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          spellcheck={false}
          enterKeyHint="done"
          maxLength={8}
          placeholder="––:––"
          value={draft}
          required={required}
          disabled={disabled}
          aria-invalid={error || invalid ? 'true' : undefined}
          aria-describedby={describedBy}
          data-temporal="time"
          onMouseDown={() => {
            pointerFocus.current = document.activeElement !== inputRef.current;
          }}
          onFocus={(e) => {
            const input = e.currentTarget as HTMLInputElement;
            if (input.value) input.select();
          }}
          onMouseUp={(e) => {
            // Anders zet de muisklik de cursor en is de selectie meteen weer weg.
            if (pointerFocus.current) {
              pointerFocus.current = false;
              e.preventDefault();
            }
          }}
          onInput={(e) => {
            const next = (e.currentTarget as HTMLInputElement).value;
            setDraft(next);
            if (invalid) setInvalid(false);
            // Een volledig getypte tijd telt meteen (dan schuift Tot al mee tijdens het typen).
            if (COMPLETE.test(next)) {
              const parsed = parseTime(next);
              if (
                parsed.ok &&
                inRange(parsed.value, min, max) &&
                parsed.value !== lastEmitted.current
              )
                emit(parsed.value);
            }
          }}
          onBlur={() => {
            typing.current = false;
            commit(draft);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              // Enter legt de tijd vast maar verstuurt het formulier niet (ook niet via "Klaar"
              // op het iOS-toetsenbord): opslaan blijft een bewuste tik op Opslaan.
              e.preventDefault();
              const input = e.currentTarget as HTMLInputElement;
              if (commit(input.value) && typing.current) input.blur();
            } else if (e.key === 'ArrowDown' && e.altKey) {
              e.preventDefault();
              openPicker(false, true);
            }
          }}
          onTouchStart={(e) => {
            const t = e.touches[0];
            touchStart.current = t ? { x: t.clientX, y: t.clientY } : null;
          }}
          onTouchEnd={(e) => {
            // Al aan het typen (of net "Zelf typen" gekozen): gewoon de cursor zetten.
            if (typing.current || disabled || document.activeElement === inputRef.current) return;
            const t = e.changedTouches[0];
            const s = touchStart.current;
            if (!t || !s || Math.abs(t.clientX - s.x) > 10 || Math.abs(t.clientY - s.y) > 10)
              return;
            e.preventDefault();
            openPicker(true);
          }}
        />
        <button
          ref={buttonRef}
          type="button"
          class={styles.clock}
          aria-label={text.open.replace('{label}', label)}
          title={text.open.replace('{label}', label)}
          aria-haspopup="dialog"
          aria-expanded={open ? 'true' : 'false'}
          aria-controls={open ? pickerId : undefined}
          disabled={disabled}
          data-name={`${id}-clock`}
          onPointerDown={(e) => {
            lastPointer.current = e.pointerType;
          }}
          onClick={(e) => {
            // detail 0 = geactiveerd met het toetsenbord (Enter/spatie).
            openPicker(e.detail > 0 && lastPointer.current === 'touch', e.detail === 0);
          }}
        >
          <Icon id="clock" size={20} />
        </button>
      </div>
      {message ? (
        <span
          id={messageId}
          class={message.kind === 'error' ? field.error : field.hint}
          role={error ? 'alert' : undefined}
        >
          {message.text}
        </span>
      ) : null}
      {open ? (
        <TimePicker
          id={pickerId}
          label={label}
          value={value}
          durationFrom={durationFrom}
          step={step}
          min={min}
          max={max}
          labels={text}
          anchor={() => boxRef.current}
          onPick={pick}
          onClose={() => setOpen(false)}
          onType={startTyping}
          quietFocus={!openedByKeyboard.current}
        />
      ) : null}
    </div>
  );
}

export interface DurationChipsProps {
  id: string;
  /** Begintijd ("HH:MM"). Zonder geldige begintijd tonen we niets. */
  start: string;
  /** Huidige eindtijd: de chip met die duur staat aan. */
  end: string;
  /** Nieuwe eindtijd. */
  onPick: (end: string) => void;
  labels?: TimeFieldLabels | undefined;
  durations?: readonly number[];
}

/**
 * Rij duurknoppen onder Van/Tot: "30 min · 16:00", "1 uur · 16:30" … (één tik zet Tot). Staat
 * over de volle breedte onder het veldenpaar; op een telefoon passen de vijf naast elkaar.
 * Zonder geldige begintijd (typfout in Van) blijven ze staan maar uit: dan springt de rest van
 * het formulier niet omhoog (review c-ux r1).
 */
export function DurationChips({
  id,
  start,
  end,
  onPick,
  labels,
  durations = DURATIONS,
}: DurationChipsProps) {
  const text = labels ?? defaultTimeLabels();
  const noStart = minutesOfTime(start) === null;
  const current = durationBetween(start, end);
  return (
    <div
      class={styles.chips}
      role="group"
      aria-labelledby={`${id}-label`}
      aria-disabled={noStart ? 'true' : undefined}
      data-name={id}
    >
      <span id={`${id}-label`} class="fb-sr-only">
        {text.durations}
      </span>
      {durations.map((d) => {
        const to = addDuration(start, d);
        const on = current === d;
        const label = formatDuration(d, text);
        return (
          <button
            key={d}
            type="button"
            class={cx(styles.chip, on && styles.chipOn)}
            aria-pressed={on ? 'true' : 'false'}
            aria-label={text.durationUntil.replace('{duration}', label).replace('{time}', to ?? '')}
            disabled={to === null}
            data-duration={d}
            onClick={() => to && onPick(to)}
          >
            <span class={styles.chipText}>{label}</span>
            <span class={styles.chipEnd}>{to ?? '–'}</span>
          </button>
        );
      })}
    </div>
  );
}

export type { TimeFieldLabels };
